import React, { useRef, useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../store";
import { useTranslation } from "react-i18next";
import { fetchChatbotById } from "../../../features/ai_chatbots/aiChatbotsSlice";
import { FaCheck, FaCopy, FaCode } from "react-icons/fa";
import { Icon } from "../../../components/common/Icon";
import AIChatbotFormModal from "./AIChatbotFormModal";

interface AIChatbotWidgetModalProps {
  show: boolean;
  onHide: () => void;
  chatbotId?: number | null;
}

const AIChatbotWidgetModal: React.FC<AIChatbotWidgetModalProps> = ({
  show,
  onHide,
  chatbotId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { selectedChatbot, isLoading } = useSelector(
    (state: RootState) => state.aiChatbots
  );
  const [copied, setCopied] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (
      show &&
      chatbotId &&
      (!selectedChatbot || selectedChatbot.id !== chatbotId)
    ) {
      dispatch(fetchChatbotById(chatbotId));
    }
  }, [show, chatbotId, selectedChatbot, dispatch]);

  const handleCopy = () => {
    if (textAreaRef.current) {
      textAreaRef.current.select();
      document.execCommand("copy");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const generateWidgetCode = () => {
    if (!selectedChatbot) return "";
    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";
    const backendBaseUrl = apiUrl.split('/api/v1')[0];
    const widgetKey = selectedChatbot.widget_key;

    return `<!-- AI Agent Widget -->
<script>
  (function() {
    var s = document.createElement('script');
    s.src = '${backendBaseUrl}/static/chatbot-widget.js';
    s.async = true;
    s.onload = function() {
      if (window.AIChatbot && typeof window.AIChatbot.init === 'function') {
        window.AIChatbot.init({ apiUrl: '${apiUrl}/public/chatbot', widgetKey: '${widgetKey}' });
      }
    };
    s.onerror = function() {
      console.warn('[Meylo] Widget script failed to load');
    };
    document.head.appendChild(s);
  })();
</script>`;
  };

  if (!selectedChatbot || isLoading) {
    return (
      <Modal show={show} onHide={onHide} centered>
        <Modal.Body className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0 shadow-lg" style={{ borderRadius: '16px' }}>
      <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
        <Modal.Title className="fw-bold h5 mb-0 d-flex align-items-center">
          <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: 40, height: 40 }}>
            <Icon icon={FaCode} size={18} />
          </div>
          {t("Add to Website")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 pt-3">
        <p className="text-muted mb-4 ps-1" style={{ fontSize: '0.95rem' }}>
          {t("Install the chatbot on your site by adding this code snippet.")}
        </p>

        {/* Minimalist Code Card similar to Google/Facebook integrations */}
        <div className="bg-light border rounded-3 p-0 mb-4 position-relative overflow-hidden group-hover-action">
          <div className="p-3 font-monospace text-dark" style={{
            fontSize: '0.85rem',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            backgroundColor: '#f8f9fa'
          }}>
            {generateWidgetCode()}
          </div>

          {/* Hidden textarea for copy command */}
          <textarea
            ref={textAreaRef}
            value={generateWidgetCode()}
            readOnly
            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
          />

          {/* Floating Copy Button */}
          <div className="position-absolute top-0 end-0 p-2">
            <Button
              size="sm"
              variant={copied ? "success" : "dark"}
              onClick={handleCopy}
              className={`d-flex align-items-center rounded-pill border-0 shadow-sm ${copied ? 'px-3' : 'px-3 text-white'}`}
              style={{ padding: '6px 14px', fontSize: '0.8rem', fontWeight: 600, transition: 'all 0.2s ease-in-out', cursor: 'pointer' }}
            >
              <Icon icon={copied ? FaCheck : FaCopy} className="me-2" />
              {copied ? t("Copied") : t("Copy")}
            </Button>
          </div>
        </div>

        {/* Simplified Instructions */}
        <div className="d-flex flex-column gap-3 ps-1">
          <div className="d-flex gap-3">
            <div className="d-flex flex-column align-items-center">
              <div className="rounded-circle bg-light text-dark fw-bold border d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '12px' }}>1</div>
              <div className="h-100 border-start my-1" style={{ borderColor: '#e9ecef' }}></div>
            </div>
            <div className="pb-2">
              <h6 className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>{t("Copy the snippet")}</h6>
              <div className="text-muted small">{t("Click the copy button on the code block above.")}</div>
            </div>
          </div>

          <div className="d-flex gap-3">
            <div>
              <div className="rounded-circle bg-light text-dark fw-bold border d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '12px' }}>2</div>
            </div>
            <div>
              <h6 className="mb-1 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>{t("Paste into your HTML")}</h6>
              <div className="text-muted small">
                {t("Paste the snippet before the closing")} <code className="text-danger bg-light px-1 rounded border">&lt;/body&gt;</code> {t("tag on every page you want the widget to appear.")}
              </div>
            </div>
          </div>
        </div>

      </Modal.Body>
      {/* No footer as requested */}
    </Modal>
  );
};

export default AIChatbotWidgetModal;
